import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, toHandlers as _toHandlers, withModifiers as _withModifiers, withKeys as _withKeys, mergeProps as _mergeProps, resolveComponent as _resolveComponent, withCtx as _withCtx, TransitionGroup as _TransitionGroup, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, resolveDirective as _resolveDirective, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-6e032d82"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "ranking-list" }
const _hoisted_2 = { class: "ranking-chart-popover-inner" }
const _hoisted_3 = {
  key: 0,
  class: "ranking-chart-popover-text"
}
const _hoisted_4 = ["tabindex", "onClick", "onKeydown"]
const _hoisted_5 = { class: "ranking-header" }
const _hoisted_6 = { class: "ranking-title" }
const _hoisted_7 = ["title"]
const _hoisted_8 = { class: "count-text" }
const _hoisted_9 = { class: "count-bar" }
const _hoisted_10 = ["tabindex", "onClick", "onKeydown"]
const _hoisted_11 = { class: "ranking-header" }
const _hoisted_12 = { class: "ranking-title" }
const _hoisted_13 = ["title"]
const _hoisted_14 = { class: "count-text" }
const _hoisted_15 = { class: "count-bar" }
const _hoisted_16 = {
  key: 0,
  class: "ranking-footer"
}

import { computed, useSlots } from 'vue'
import Card from '@/views/dashboard/_components/card/card.vue'
import TimeRangeSelect from '../time-range-select/time-range-select.vue';
import { TimeRange } from '../time-range-select/time-range.enum';
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue';

export interface RankingItem {
  [key: string]: any
  count: number
  /**
   * 单项原生事件对象，与内置 `click` / `keydown.enter` 合并（`v-on` 对象语法）。
   * 统计类、埋点等扩展监听请写在这里，避免改 RankingChart 模板。
   */
  events?: Record<string, (...args: unknown[]) => unknown>
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ranking-chart',
  props: {
    title: {},
    description: { default: '' },
    data: { default: () => [] },
    cardOpt: { default: () => ({}) },
    timeRange: { default: TimeRange.DAYS_30 },
    showTimeRange: { type: Boolean, default: true },
    cardSize: { default: 'second' },
    unit: { default: '次' },
    nameKey: { default: 'name' },
    emptyHeight: { default: '100%' },
    maxItems: { default: 5 },
    cardHeaderStyle: { default: () => ({ 'margin-bottom': '15px' }) },
    showIndex: { type: Boolean, default: true },
    colors: { default: () => [] },
    usePercentage: { type: Boolean, default: false },
    percentageKey: { default: 'percentage' },
    showAll: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    rowClickable: { type: Boolean, default: false },
    rowPopover: { type: Boolean, default: false },
    popoverWidth: { default: 360 },
    popoverContentKey: { default: '' },
    popoverPlacement: { default: 'top' },
    popoverPopperClass: { default: 'ranking-chart-popper' },
    itemKeyField: { default: '' }
  },
  emits: ["update:timeRange", "update:loading", "itemClick"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const slots = useSlots()

function onRowClick (item: RankingItem) {
  if (!props.rowClickable) return
  emit('itemClick', item)
}

function getRowKey (item: RankingItem, index: number) {
  const k = props.itemKeyField
  if (k && item[k] != null && String(item[k]) !== '') {
    return String(item[k])
  }
  return `${item.rank}-${item.count}-${index}`
}

function getPopoverPlainText (item: RankingItem) {
  const key = props.popoverContentKey
  if (!key) return ''
  const raw = item[key]
  if (raw == null) return ''
  return String(raw).trim()
}

function hasPopoverContent (item: RankingItem) {
  if (slots['item-popover']) return true
  return Boolean(props.popoverContentKey && getPopoverPlainText(item))
}

const timeRange = computed({
  get: () => props.timeRange,
  set: (val) => emit('update:timeRange', val)
})

const sortedData = computed(() => {
  return [...props.data]
    .sort((a, b) => {
      if (props.usePercentage) {
        const aVal = a[props.percentageKey] || 0
        const bVal = b[props.percentageKey] || 0
        return bVal - aVal
      }
      return b.count - a.count
    })
    .slice(0, props.maxItems)
    .map((item, index) => ({
      ...item,
      rank: index + 1
    }))
})

const hasData = computed(() => {
  return sortedData.value.length > 0
})

const maxCount = computed(() => {
  if (props.usePercentage) {
    return 100 // 百分比模式，最大值是100
  }
  return Math.max(...sortedData.value.map(item => item.count), 1)
})

function getCountPercentage(item: RankingItem): number {
  if (props.usePercentage) {
    return item[props.percentageKey] || 0
  }
  const count = item.count || 0
  return maxCount.value > 0 ? (count / maxCount.value) * 100 : 0
}

function getProgressBarStyle(item: RankingItem, index: number) {
  const width = getCountPercentage(item)
  const style: Record<string, string> = {
    width: `${width}%`
  }

  // 如果提供了自定义颜色数组，使用对应的颜色
  if (props.colors && props.colors.length > 0) {
    style.backgroundColor = props.colors[index % props.colors.length]
  } else {
    style.backgroundColor = index < 3 ? 'var(--el-color-primary)' : 'var(--cm-color-text-secondary)'
  }

  return style
}

function getPercentageValue(item: RankingItem): number {
  return item[props.percentageKey] || 0
}

function getRankClass(rank: number): string {
  if (rank <= 3) {
    return `rank-${rank}`
  }
  return 'rank-inactive'
}

function getItemName(item: RankingItem): string {
  if (props.nameKey === 'name' && item.department) {
    return `${item.name} (${item.department})`
  }
  return item[props.nameKey] || item.name || ''
}

return (_ctx: any,_cache: any) => {
  const _component_el_popover = _resolveComponent("el-popover")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(Card, _mergeProps({
    size: _ctx.cardSize,
    title: _ctx.title,
    description: _ctx.description,
    class: "ranking-chart-card",
    cardHeaderStyle: _ctx.cardHeaderStyle
  }, _ctx.cardOpt), {
    titleAfter: _withCtx(() => [
      _renderSlot(_ctx.$slots, "titleAfter")
    ]),
    headerRight: _withCtx(() => [
      _renderSlot(_ctx.$slots, "headerRight", {}, () => [
        (_ctx.showTimeRange)
          ? (_openBlock(), _createBlock(TimeRangeSelect, {
              key: 0,
              modelValue: timeRange.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((timeRange).value = $event)),
              "show-all": _ctx.showAll
            }, null, 8, ["modelValue", "show-all"]))
          : _createCommentVNode("", true)
      ])
    ]),
    default: _withCtx(() => [
      _withDirectives(_createElementVNode("div", _hoisted_1, [
        _createVNode(_TransitionGroup, {
          name: "ranking-item",
          tag: "div",
          class: "ranking-list-inner"
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedData.value, (item, index) => {
              return (_openBlock(), _createElementBlock(_Fragment, null, [
                (_ctx.rowPopover)
                  ? (_openBlock(), _createBlock(_component_el_popover, {
                      key: `${getRowKey(item, index)}-popover`,
                      class: "ranking-chart-el-popover",
                      disabled: !hasPopoverContent(item),
                      placement: _ctx.popoverPlacement,
                      width: _ctx.popoverWidth,
                      trigger: "hover",
                      "show-arrow": true,
                      "popper-class": _ctx.popoverPopperClass
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                          _renderSlot(_ctx.$slots, "item-popover", { item: item }, () => [
                            (_ctx.popoverContentKey && getPopoverPlainText(item))
                              ? (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(getPopoverPlainText(item)), 1))
                              : _createCommentVNode("", true)
                          ])
                        ])
                      ]),
                      reference: _withCtx(() => [
                        _createElementVNode("div", _mergeProps({
                          class: ["ranking-item", { 'is-clickable': _ctx.rowClickable }],
                          role: "button",
                          tabindex: _ctx.rowClickable ? 0 : undefined
                        }, _toHandlers(item.events ?? {}, true), {
                          onClick: ($event: any) => (onRowClick(item)),
                          onKeydown: _withKeys(_withModifiers(() => { if (_ctx.rowClickable) onRowClick(item) }, ["prevent"]), ["enter"])
                        }), [
                          _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("div", _hoisted_6, [
                              (_ctx.showIndex)
                                ? (_openBlock(), _createElementBlock("div", {
                                    key: 0,
                                    class: _normalizeClass(["rank-badge", getRankClass(item.rank)])
                                  }, _toDisplayString(item.rank), 3))
                                : _createCommentVNode("", true),
                              _createElementVNode("div", {
                                class: _normalizeClass(["item-name", { 'no-index': !_ctx.showIndex }]),
                                title: getItemName(item)
                              }, [
                                _renderSlot(_ctx.$slots, "item-name", { item: item }, () => [
                                  _createTextVNode(_toDisplayString(getItemName(item)), 1)
                                ])
                              ], 10, _hoisted_7)
                            ]),
                            _createElementVNode("span", _hoisted_8, [
                              _renderSlot(_ctx.$slots, "count-text", { item: item }, () => [
                                (_ctx.usePercentage)
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createTextVNode(_toDisplayString(getPercentageValue(item)) + "% ", 1)
                                    ], 64))
                                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                      _createTextVNode(_toDisplayString(item.count) + _toDisplayString(_ctx.unit), 1)
                                    ], 64))
                              ])
                            ])
                          ]),
                          _createElementVNode("div", {
                            class: _normalizeClass(["ranking-bar-wrapper", { 'no-index': !_ctx.showIndex }])
                          }, [
                            _createElementVNode("div", _hoisted_9, [
                              _createElementVNode("div", {
                                class: "count-fill",
                                style: _normalizeStyle(getProgressBarStyle(item, index))
                              }, null, 4)
                            ])
                          ], 2)
                        ], 16, _hoisted_4)
                      ]),
                      _: 2
                    }, 1032, ["disabled", "placement", "width", "popper-class"]))
                  : (_openBlock(), _createElementBlock("div", {
                      key: `${getRowKey(item, index)}-plain`,
                      class: "ranking-chart-el-popover ranking-chart-row-plain"
                    }, [
                      _createElementVNode("div", _mergeProps({
                        class: ["ranking-item", { 'is-clickable': _ctx.rowClickable }],
                        role: "button",
                        tabindex: _ctx.rowClickable ? 0 : undefined
                      }, _toHandlers(item.events ?? {}, true), {
                        onClick: ($event: any) => (onRowClick(item)),
                        onKeydown: _withKeys(_withModifiers(() => { if (_ctx.rowClickable) onRowClick(item) }, ["prevent"]), ["enter"])
                      }), [
                        _createElementVNode("div", _hoisted_11, [
                          _createElementVNode("div", _hoisted_12, [
                            (_ctx.showIndex)
                              ? (_openBlock(), _createElementBlock("div", {
                                  key: 0,
                                  class: _normalizeClass(["rank-badge", getRankClass(item.rank)])
                                }, _toDisplayString(item.rank), 3))
                              : _createCommentVNode("", true),
                            _createElementVNode("div", {
                              class: _normalizeClass(["item-name", { 'no-index': !_ctx.showIndex }]),
                              title: getItemName(item)
                            }, [
                              _renderSlot(_ctx.$slots, "item-name", { item: item }, () => [
                                _createTextVNode(_toDisplayString(getItemName(item)), 1)
                              ])
                            ], 10, _hoisted_13)
                          ]),
                          _createElementVNode("span", _hoisted_14, [
                            _renderSlot(_ctx.$slots, "count-text", { item: item }, () => [
                              (_ctx.usePercentage)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createTextVNode(_toDisplayString(getPercentageValue(item)) + "% ", 1)
                                  ], 64))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createTextVNode(_toDisplayString(item.count) + _toDisplayString(_ctx.unit), 1)
                                  ], 64))
                            ])
                          ])
                        ]),
                        _createElementVNode("div", {
                          class: _normalizeClass(["ranking-bar-wrapper", { 'no-index': !_ctx.showIndex }])
                        }, [
                          _createElementVNode("div", _hoisted_15, [
                            _createElementVNode("div", {
                              class: "count-fill",
                              style: _normalizeStyle(getProgressBarStyle(item, index))
                            }, null, 4)
                          ])
                        ], 2)
                      ], 16, _hoisted_10)
                    ]))
              ], 64))
            }), 256))
          ]),
          _: 3
        })
      ], 512), [
        [_vShow, hasData.value]
      ]),
      _withDirectives(_createVNode(EmptyData, {
        height: _ctx.emptyHeight,
        "bg-color": "transparent"
      }, null, 8, ["height"]), [
        [_vShow, !hasData.value]
      ]),
      (_ctx.$slots.footer)
        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
            _renderSlot(_ctx.$slots, "footer")
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 3
  }, 16, ["size", "title", "description", "cardHeaderStyle"])), [
    [_directive_loading, props.loading]
  ])
}
}

})